import { api, apiQueryKey } from '@/shared/api'
import {
    LinkDescriptor,
    WorkspaceDescriptor,
    WorkspaceWithLinkDescriptor,
} from '@synapex/shared2/entities'
import {
    CreativeMarkingData,
    SendToTelegramBody,
    SendToTelegramReply,
} from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    UseMutationOptions,
    usePrefetchQuery,
    useQuery,
    useQueryClient,
    type UseQueryOptions,
} from '@tanstack/react-query'
import {
    AnyLinkWithAnalyticsType,
    type ICreative,
    QUERY_KEY,
} from './creatives.defs'
import {
    CREATIVE_TYPE_CONTEXT,
    useCreativeMutationWithOptimisticCacheUpdate,
} from './helpers'
import { Creative } from '@synapex/shared2/gateway-rest-api'
import {
    channelLinkMany,
    siteLinkMany,
} from '@/true-fsd/data/link/react-query/key-fn'

type QueryOptions = Required<
    Pick<
        UseQueryOptions<ICreative[], Error>,
        'refetchOnMount' | 'refetchOnWindowFocus'
    >
>

const QUERY_STALE_TIME = 60 * 1000

export function useCreativesQuery(
    { workspace_id, workspace_type }: WorkspaceDescriptor,
    { refetchOnMount, refetchOnWindowFocus }: QueryOptions
) {
    return useQuery<Creative.ManyReply, Error>({
        queryKey: [QUERY_KEY.creatives, workspace_type, workspace_id],
        queryFn: () =>
            api.get(`/creatives`, { params: { workspace_id, workspace_type } }),
        staleTime: QUERY_STALE_TIME,
        refetchOnMount,
        refetchOnWindowFocus,
    })
}

export function useCreativesPrefetchQuery({
    workspace_id,
    workspace_type,
}: WorkspaceDescriptor) {
    usePrefetchQuery<Creative.ManyReply, Error>({
        queryKey: [QUERY_KEY.creatives, workspace_type, workspace_id],
        queryFn: () =>
            api.get(`/creatives`, { params: { workspace_id, workspace_type } }),
        staleTime: QUERY_STALE_TIME,
    })
}

export const useCreativeDeleteById = (
    {
        workspace_id,
        workspace_type,
        groupId,
    }: WorkspaceDescriptor & {
        groupId: number
    },
    opts: Parameters<
        typeof useCreativeMutationWithOptimisticCacheUpdate<{
            creativeId: number
        }>
    >[1]
) =>
    useCreativeMutationWithOptimisticCacheUpdate<{
        creativeId: number
    }>(
        {
            workspace_type,
            workspace_id,
            groupId,
            mutationFn: ({ creativeId }) =>
                api.delete(`/creatives/${creativeId}`),
            creativeUpdater: () => undefined,
            linkUpdater: (link, _updatedCreative, published) => ({
                ...link,
                active_creative: published
                    ? {
                          ...link.active_creative,
                          //mark as deleted if it already published
                          is_deleted: true,
                      }
                    : //clear if non-published
                      null,
            }),
        },
        opts
    )

export type UseCreativeDeleteById = ReturnType<typeof useCreativeDeleteById>

export const useCreativeUpdateNameById = (
    {
        workspace_type,
        workspace_id,
        groupId,
    }: WorkspaceDescriptor & {
        groupId: number
    },
    opts: Parameters<
        typeof useCreativeMutationWithOptimisticCacheUpdate<{
            creativeId: number
            name: string | null
        }>
    >[1]
) =>
    useCreativeMutationWithOptimisticCacheUpdate<{
        creativeId: number
        name: string | null
    }>(
        {
            workspace_type,
            workspace_id,
            groupId,
            mutationFn: ({ creativeId, name }) =>
                api.put(`/creatives/${creativeId}`, { name }),
            creativeUpdater: (creative, published, { name }) =>
                published ? creative : { ...creative, extended_title: name },
            linkUpdater: (link, updatedCreative, published) =>
                published
                    ? link
                    : {
                          ...link,
                          active_creative: updatedCreative ?? null,
                      },
        },
        opts
    )

export type UseCreativeUpdateNameById = ReturnType<
    typeof useCreativeUpdateNameById
>

export const useSetLinkCreativeMutation = (
    { link_id, workspace_id, workspace_type }: WorkspaceWithLinkDescriptor,
    opts: Pick<
        UseMutationOptions<
            unknown,
            Error,
            {
                creative: ICreative
            }
        >,
        'onError' | 'onSuccess'
    >
) => {
    const client = useQueryClient()
    const typeContext = CREATIVE_TYPE_CONTEXT[workspace_type]

    const linksQueryKey =
        workspace_type === 'site'
            ? siteLinkMany({ site_id: workspace_id }).queryKey
            : channelLinkMany({ channel_id: workspace_id }).queryKey

    return useMutation<
        unknown,
        Error,
        {
            creative: ICreative
        },
        { backup: AnyLinkWithAnalyticsType[] }
    >({
        mutationFn: ({ creative }) =>
            api.put(`/link/${workspace_type}/${link_id}/creative`, {
                active_creative_id: creative.id,
            }),

        onSuccess: (data, variables, context) => {
            void client.invalidateQueries({
                queryKey: apiQueryKey.vkOrdCreatives(
                    {
                        link_id,
                        workspace_id,
                        workspace_type,
                    },
                    variables.creative.id
                ),
            })

            // Инвалидируем кэш маркировки, чтобы обновить состояние кнопки после выбора креатива
            void client.invalidateQueries({
                queryKey: apiQueryKey.markingFormData({
                    link_id,
                    workspace_id,
                    workspace_type,
                }),
            })

            if (opts.onSuccess) {
                opts.onSuccess(data, variables, context)
            }
        },
        onMutate: async (dto) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await client.cancelQueries({
                queryKey: linksQueryKey,
            })

            const backup =
                client.getQueryData<AnyLinkWithAnalyticsType[]>(
                    linksQueryKey
                ) ?? []

            // Optimistically update to the new value
            client.setQueryData<AnyLinkWithAnalyticsType[]>(
                linksQueryKey,
                (old) =>
                    old
                        ? old.map((link) => {
                              if (link[typeContext.linkIdProp] === link_id) {
                                  return {
                                      ...link,
                                      active_creative_id: dto.creative.id,
                                      active_creative: dto.creative,
                                  }
                              }

                              return link
                          })
                        : []
            )

            // Return a context object with the snapshotted value
            return { backup }
        },
        onError: (error, _variables, context) => {
            console.error(error)
            if (context) {
                client.setQueryData<AnyLinkWithAnalyticsType[]>(
                    linksQueryKey,
                    context.backup
                )
            }
            if (opts.onError) {
                opts.onError(error, _variables, context)
            }
        },

        onSettled: () => {
            void client.invalidateQueries({
                queryKey: linksQueryKey,
            })
            void client.invalidateQueries({
                queryKey: [typeContext.groupsQueryKey],
            })
        },
    })
}

export type UseSetLinkCreativeMutation = ReturnType<
    typeof useSetLinkCreativeMutation
>

export const useShareCreative = () =>
    useMutation<
        SendToTelegramReply,
        Error,
        LinkDescriptor & {
            marking?: CreativeMarkingData
        }
    >({
        mutationFn: async ({
            workspace_type,
            link_id,
            marking,
        }: LinkDescriptor & {
            marking?: CreativeMarkingData
        }) => {
            const body: SendToTelegramBody =
                workspace_type === 'site'
                    ? { siteLinkId: link_id, marking }
                    : { channelLinkId: link_id, marking }
            return api.post<SendToTelegramReply>(
                `/creatives/send-to-telegram`,
                body
            )
        },
    })
